import api from '~/store/services/api';
import { SMTMealType } from '~/components/maud/patients/profile/smt/types';
import { CacheTag } from '~/store/services/CacheTag';
import { HttpRequestMethod } from '~/store';
import {
  DailyLogsResponse,
  DateRange,
  GetSMTLogsParams,
  getSMTDailyLogsInitialPageParam,
  getSMTDailyLogsNextPageParam,
} from '~/lib/util/smt.utils';

interface GetSMTPermissionsResponse {
  smtEnabled: boolean;
  permissions: SMTUserPermission[];
}

interface UpdateSMTPermissionsRequest {
  patientId: string;
  smtEnabled?: boolean;
  permissions?: Pick<SMTUserPermission, 'userId' | 'permissionType'>[];
}

interface SMTEntryBase {
  mealType?: SMTMealType;
  description?: string;
  comment?: string;
  calories?: number | null;
  isNewFood?: boolean;
  occurredAt: string;
}

interface SMTEntryIdentifier {
  patientId: string;
  smtEntryId: string;
}

interface CreateSMTMealRequest extends SMTEntryBase {
  patientId: string;
}

interface CreateSMTMealResponse {
  smtEntryId: string;
}

interface UpdateSMTEntryRequest extends SMTEntryBase, SMTEntryIdentifier {}

interface AddSMTEntryCommentRequest extends SMTEntryIdentifier {
  comment: string;
}

interface DeleteSMTEntryRequest extends SMTEntryIdentifier {}

interface MealImageUploadResponse {
  fileServiceKey: string;
  fileScanStatus: string;
}

interface UploadSMTEntryImageRequest extends SMTEntryIdentifier {
  formData: FormData;
}

interface ReplaceSMTEntryImageRequest extends SMTEntryIdentifier {
  imageId: string;
  formData: FormData;
}

export interface SMTSummaryEntry {
  date: string; // YYYY-MM-DD format (e.g., "2025-09-01")
  entryCount: number; // Number of entries for that date
}

export interface GetSMTSummaryResponse {
  entries: SMTSummaryEntry[];
}

export interface GetSMTSummaryRequest {
  patientId: string;
  startDate: string; // YYYY-MM-DD format
  endDate: string; // YYYY-MM-DD format
}

export const smtApi = api.injectEndpoints({
  endpoints: (build) => ({
    addSMTEntryComment: build.mutation<void, AddSMTEntryCommentRequest>({
      invalidatesTags: (_result, _error, { patientId }) => [
        { id: patientId, type: CacheTag.SMTDailyLogs },
      ],
      query: ({ patientId, smtEntryId, comment }) => ({
        body: {
          comment,
        },
        method: HttpRequestMethod.POST,
        url: `assessments/patients/${patientId}/smt/${smtEntryId}/comment`,
      }),
    }),
    createSMTMeal: build.mutation<CreateSMTMealResponse, CreateSMTMealRequest>({
      invalidatesTags: (_result, _error, { patientId }) => [
        { id: patientId, type: CacheTag.SMTDailyLogs },
        { id: patientId, type: CacheTag.SMTSummary },
      ],
      query: ({ patientId, ...body }) => ({
        body,
        method: HttpRequestMethod.POST,
        url: `assessments/patients/${patientId}/meals`,
      }),
    }),
    deleteSMTEntry: build.mutation<void, DeleteSMTEntryRequest>({
      invalidatesTags: (_result, _error, { patientId }) => [
        { id: patientId, type: CacheTag.SMTDailyLogs },
        { id: patientId, type: CacheTag.SMTSummary },
      ],
      query: ({ patientId, smtEntryId }) => ({
        body: {},
        method: HttpRequestMethod.DELETE,
        url: `assessments/patients/${patientId}/smt/${smtEntryId}`,
      }),
    }),
    getSMTDailyLogs: build.infiniteQuery<
      DailyLogsResponse,
      GetSMTLogsParams,
      DateRange
    >({
      providesTags: (_result, _error, { patientId }) => [
        { id: patientId, type: CacheTag.SMTDailyLogs },
      ],
      query: ({ queryArg, pageParam }) => {
        // Calculation logic: Use pageParam if scrolling, otherwise calculate first chunk
        const range =
          pageParam ?? getSMTDailyLogsInitialPageParam(queryArg.selectedDate);
        return {
          params: {
            endDate: range.endDate,
            startDate: range.startDate,
          },
          url: `assessments/patients/${queryArg.patientId}/smt/daily-logs`,
        };
      },
      infiniteQueryOptions: {
        // initialPageParam must be passed at the call site using getSMTDailyLogsInitialPageParam(selectedDate).
        // This is necessary because the initial date range depends on the runtime selectedDate value.
        // The query function has a fallback (line 148-150) but callers should always provide it explicitly.
        initialPageParam: undefined,
        getNextPageParam: getSMTDailyLogsNextPageParam,
      },
    }),
    getSMTPermissions: build.query<GetSMTPermissionsResponse, string>({
      providesTags: [CacheTag.SMTConfigurations],
      query: (patientId) => ({
        method: 'GET',
        url: `assessments/patients/${patientId}/smt/permissions`,
      }),
    }),
    getSMTSummary: build.query<GetSMTSummaryResponse, GetSMTSummaryRequest>({
      providesTags: (_result, _error, { patientId }) => [
        { id: patientId, type: CacheTag.SMTSummary },
      ],
      query: ({ patientId, startDate, endDate }) => ({
        params: {
          endDate,
          startDate,
        },
        url: `assessments/patients/${patientId}/smt/summary`,
      }),
    }),
    replaceSMTEntryImage: build.mutation<
      MealImageUploadResponse,
      ReplaceSMTEntryImageRequest
    >({
      invalidatesTags: (_result, _error, { patientId }) => [
        { id: patientId, type: CacheTag.SMTDailyLogs },
      ],
      query: ({ patientId, smtEntryId, imageId, formData }) => ({
        body: formData,
        method: HttpRequestMethod.PUT,
        url: `assessments/patients/${patientId}/smt/${smtEntryId}/images/${imageId}`,
      }),
    }),
    updateSMTEntry: build.mutation<void, UpdateSMTEntryRequest>({
      invalidatesTags: (_result, _error, { patientId }) => [
        { id: patientId, type: CacheTag.SMTDailyLogs },
        { id: patientId, type: CacheTag.SMTSummary },
      ],
      query: ({ patientId, smtEntryId, ...body }) => ({
        body,
        method: HttpRequestMethod.PUT,
        url: `assessments/patients/${patientId}/smt/${smtEntryId}/meals`,
      }),
    }),
    updateSMTPermissions: build.mutation<void, UpdateSMTPermissionsRequest>({
      invalidatesTags: [CacheTag.SMTConfigurations],
      onQueryStarted: async (
        { patientId, smtEnabled, permissions },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          smtApi.util.updateQueryData(
            'getSMTPermissions',
            patientId,
            (draft) => {
              if (smtEnabled !== undefined) {
                draft.smtEnabled = smtEnabled;
              }
              if (permissions) {
                // Optimistically update permissions in the cache
                // This is safe because the component only allows updating users
                // that already exist in the permissions array
                draft.permissions = draft.permissions.map((p) => {
                  const updatedPermission = permissions.find(
                    (up) => up.userId === p.userId,
                  );
                  return updatedPermission
                    ? { ...p, permissionType: updatedPermission.permissionType }
                    : p;
                });
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      query: ({ patientId, smtEnabled, permissions }) => ({
        body: {
          ...(smtEnabled !== undefined && { smtEnabled }),
          ...(permissions && { permissions }),
        },
        method: 'PUT',
        url: `assessments/patients/${patientId}/smt/permissions`,
      }),
    }),
    uploadSMTEntryImage: build.mutation<
      MealImageUploadResponse,
      UploadSMTEntryImageRequest
    >({
      invalidatesTags: (_result, _error, { patientId }) => [
        { id: patientId, type: CacheTag.SMTDailyLogs },
      ],
      query: ({ patientId, smtEntryId, formData }) => ({
        body: formData,
        method: HttpRequestMethod.POST,
        url: `assessments/patients/${patientId}/smt/${smtEntryId}/images`,
      }),
    }),
  }),
});

export const {
  useAddSMTEntryCommentMutation,
  useCreateSMTMealMutation,
  useDeleteSMTEntryMutation,
  useGetSMTDailyLogsInfiniteQuery,
  useGetSMTPermissionsQuery,
  useGetSMTSummaryQuery,
  useLazyGetSMTSummaryQuery,
  useReplaceSMTEntryImageMutation,
  useUpdateSMTEntryMutation,
  useUpdateSMTPermissionsMutation,
  useUploadSMTEntryImageMutation,
} = smtApi;

export type { CreateSMTMealRequest, UpdateSMTEntryRequest };
